import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { acceptRequiredConsents } from "../../api/consentsApi";
import { useAuth } from "../../auth/useAuth";
import { publicPathForDocumentType } from "../../utils/legalRoutes";

const ConsentRequiredPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, isLoading } = useAuth();
  const [acceptedIds, setAcceptedIds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const missing = user?.consent?.missingRequiredConsents || [];

  const returnTo = useMemo(() => {
    const from = location.state?.returnTo;
    if (typeof from === "string" && from.startsWith("/") && !from.startsWith("/account/consent-required")) {
      return from;
    }

    return "/account/dashboard";
  }, [location.state?.returnTo]);

  useEffect(() => {
    if (!isLoading && user && !user.consent?.requiresConsent) {
      navigate(returnTo, { replace: true });
    }
  }, [isLoading, user, navigate, returnTo]);

  const allChecked =
    missing.length > 0 && missing.every((doc) => acceptedIds[doc.legalDocumentId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);

    try {
      await acceptRequiredConsents(
        missing.map((doc) => doc.legalDocumentId),
        "login"
      );
      await refreshUser();
      navigate(returnTo, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || "Unable to record acceptance.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="panel-card">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (!missing.length) {
    return (
      <section className="panel-card">
        <p className="muted">No pending consent requirements.</p>
      </section>
    );
  }

  return (
    <section className="panel-card">
      <h2>Consent required</h2>
      <p className="muted">
        To continue using your account, please review and accept the current legal documents
        listed below.
      </p>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}

      <form className="admin-users-form" onSubmit={handleSubmit}>
        <ul className="legal-consent-list">
          {missing.map((doc) => (
            <li key={doc.legalDocumentId}>
              <label className="admin-users-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(acceptedIds[doc.legalDocumentId])}
                  onChange={(event) =>
                    setAcceptedIds((current) => ({
                      ...current,
                      [doc.legalDocumentId]: event.target.checked,
                    }))
                  }
                />
                I accept the{" "}
                <Link to={publicPathForDocumentType(doc.documentType)} target="_blank" rel="noreferrer">
                  {doc.title}
                </Link>{" "}
                (version {doc.version})
              </label>
              {doc.summary ? <p className="muted">{doc.summary}</p> : null}
            </li>
          ))}
        </ul>

        <button type="submit" className="report-submit-cta" disabled={!allChecked || submitting}>
          {submitting ? "Saving…" : "Accept and continue"}
        </button>
      </form>
    </section>
  );
};

export default ConsentRequiredPage;
